import { useEffect, useState } from "react";
import API, { getErrorMessage } from "../api/axios";
import { getUser, hasFullAccess } from "../auth/auth";
import { useToast } from "../components/Toast";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  MenuItem,
  Stack,
  Avatar,
  Chip,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { ACCENT, ACCENT_SOFT, NAVY } from "../theme";
import PageContainer from "../components/ui/PageContainer";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";

const roleLabels = {
  ADMIN: "Administrator",
  MANAGER: "HR Manager",
  RECRUITER: "Recruiter",
  STAFF: "Staff",
  CLIENT: "Client",
};

// Simple relative-time formatter for the real "Last changed" indicator —
// a plain elapsed-time calculation, no timezone conversion involved (same
// category as the existing relativeAge() helper on Tickets.jsx).
function daysAgo(isoString) {
  const ms = Date.now() - new Date(isoString).getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

export default function Settings() {
  const toast = useToast();
  const user = getUser() || {};
  const isAdmin = (user.role || "") === "ADMIN";

  // ── change own password ──
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  // ── admin: reset any user's password ──
  const [users, setUsers] = useState([]);
  const [targetEmail, setTargetEmail] = useState("");
  const [resetPw, setResetPw] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  // ── admin: create new user ──
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    company: "",
  });
  const [showNewPw, setShowNewPw] = useState(false);
  const [creating, setCreating] = useState(false);

  // ── Phase 14 (Design Gap Audit item N): visual-parity additions ──────────
  // Sub-navigation, matching the prototype's intent ("split into a
  // sub-navigation, with destructive admin actions separated") — but ONLY
  // for sections that have real content. The prototype's full nav list
  // also includes Notifications, Workspace, Pipeline stages, Rejection
  // reasons, Data & export, and Audit log — none of which have any
  // backing data or functionality anywhere in this application. Per the
  // instruction not to create fake tabs with fake data, those are left out
  // entirely rather than added as dead links or empty placeholder pages.
  const sections = [
    { key: "profile", label: "My profile", icon: AccountCircleOutlinedIcon },
    { key: "password", label: "Security & password", icon: LockOutlinedIcon },
    ...(isAdmin
      ? [{ key: "admin", label: "Admin actions", icon: ShieldOutlinedIcon }]
      : []),
  ];
  const [section, setSection] = useState("profile");

  // Confirm before the admin reset actually fires — this changes another
  // user's access immediately, so it gets the same confirm-before-destructive
  // treatment as the prototype calls for. Same adminReset() logic below,
  // unchanged — only gated behind an explicit confirm step now.
  const [confirmingReset, setConfirmingReset] = useState(false);

  // ── Feature E (Settings — Editable Profile) ─────────────────────────────
  // localStorage's stored user object (see auth.js saveAuth/getUser) has
  // never included `phone` — only name/email/role/company — so it's fetched
  // fresh from the new GET /account/profile endpoint. Saving PUTs only
  // name/phone (the only two fields this endpoint accepts — see
  // UserController.updateOwnProfile) and then updates localStorage's name
  // so the sidebar/header picks up the change immediately, without
  // requiring the user to log out and back in.
  const [profileName, setProfileName] = useState(user.name || "");
  const [profilePhone, setProfilePhone] = useState("");
  const [passwordChangedAt, setPasswordChangedAt] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  useEffect(() => {
    API.get("/account/profile")
      .then((r) => {
        setProfilePhone(r.data?.phone || "");
        setPasswordChangedAt(r.data?.passwordChangedAt || null);
      })
      .catch(() => {}); // non-critical — fields just stay blank if this fails
  }, []);

  const saveProfile = async () => {
    if (!profileName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setSavingProfile(true);
    try {
      await API.put("/account/profile", {
        name: profileName.trim(),
        phone: profilePhone,
      });
      // Keep the existing saveAuth/getUser shape exactly (name/email/role/
      // company) — only the name value changes, everything else preserved
      // as-is, so nothing else relying on this localStorage entry breaks.
      const stored = getUser() || {};
      localStorage.setItem(
        "user",
        JSON.stringify({ ...stored, name: profileName.trim() }),
      );
      toast.success("Profile updated");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not update profile"));
    } finally {
      setSavingProfile(false);
    }
  };

  // ── admin: deactivate a user shortcut (new — reuses the EXISTING
  //    DELETE /account/users/{id} endpoint already used by Users.jsx;
  //    no new backend logic, just exposing the same real action here too,
  //    matching the prototype's "Admin actions" grouping). ──
  const [deactivateTarget, setDeactivateTarget] = useState(""); // user id, as string (Select value)
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      API.get("/account/users")
        .then((r) => setUsers(r.data))
        .catch(() => {});
    }
  }, [isAdmin]);

  const initials = (user.name || "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // ── handlers ──

  const changePassword = async () => {
    if (!cur || !next) {
      toast.error("Please fill in both password fields");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSavingPw(true);
    try {
      await API.post("/account/change-password", {
        currentPassword: cur,
        newPassword: next,
      });
      toast.success("Password changed successfully");
      setCur("");
      setNext("");
      setConfirm("");
      setPasswordChangedAt(new Date().toISOString());
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not change password"));
    } finally {
      setSavingPw(false);
    }
  };

  const adminReset = async () => {
    setResetting(true);
    try {
      const { data } = await API.post("/account/admin-reset-password", {
        email: targetEmail,
        newPassword: resetPw,
      });
      toast.success(data?.message || "Password reset");
      setTargetEmail("");
      setResetPw("");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not reset password"));
    } finally {
      setResetting(false);
      setConfirmingReset(false);
    }
  };

  const requestReset = () => {
    if (!targetEmail) {
      toast.error("Select a user to reset");
      return;
    }
    if (!resetPw || resetPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setConfirmingReset(true);
  };

  const deactivateUser = async () => {
    setDeactivating(true);
    try {
      await API.delete(`/account/users/${deactivateTarget}`);
      toast.success("User deactivated");
      setDeactivateTarget("");
      // Refresh the list so the deactivated user disappears from both
      // dropdowns on this page.
      API.get("/account/users")
        .then((r) => setUsers(r.data))
        .catch(() => {});
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not deactivate user"));
    } finally {
      setDeactivating(false);
      setConfirmingDeactivate(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password || !newUser.role) {
      toast.error("All fields are required");
      return;
    }
    if (newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    // A client account must have a company (used to match their positions)
    if (newUser.role === "CLIENT" && !newUser.company.trim()) {
      toast.error("Company is required for a Client account");
      return;
    }
    setCreating(true);
    try {
      await API.post("/account/create-user", newUser);
      toast.success(`User "${newUser.name}" created successfully`);
      setNewUser({ name: "", email: "", password: "", role: "", company: "" });
      // refresh the users list so the new user appears in Reset dropdown
      API.get("/account/users")
        .then((r) => setUsers(r.data))
        .catch(() => {});
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not create user"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <PageContainer
      sx={{
        px: { xs: 2, md: 2.5 },
        py: { xs: 2, md: 2 },
        maxWidth: 1100,
        mx: "auto",
      }}
    >
      <Box sx={{ mb: 2, pl: { xs: 5, md: 0 } }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: NAVY }}>
          Settings
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 14, mt: 0.25 }}>
          Manage your account and security
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* ── Sub-navigation ── */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 1 }}>
            <Stack spacing={0.5}>
              {sections.map((s) => {
                const Icon = s.icon;
                const active = section === s.key;
                return (
                  <Button
                    key={s.key}
                    onClick={() => setSection(s.key)}
                    startIcon={<Icon sx={{ fontSize: 18 }} />}
                    sx={{
                      justifyContent: "flex-start",
                      px: 1.5,
                      py: 1,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: active ? 700 : 500,
                      fontSize: 13,
                      color: active ? "primary.main" : "text.secondary",
                      bgcolor: active ? "primary.light" : "transparent",
                      "&:hover": {
                        bgcolor: active ? "primary.light" : "action.hover",
                      },
                    }}
                  >
                    {s.label}
                  </Button>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        {/* ── Section content ── */}
        <Grid item xs={12} md={9}>
          {section === "profile" && (
            <Paper sx={{ p: { xs: 2.5, md: 3 }, mb: 3 }}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mb: 2.5 }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: NAVY,
                    fontSize: 20,
                    fontWeight: 700,
                  }}
                >
                  {initials}
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                    {user.name || "User"}
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                    {user.email}
                  </Typography>
                  <Chip
                    size="small"
                    label={roleLabels[user.role] || user.role}
                    sx={{
                      mt: 0.5,
                      bgcolor: `${ACCENT}14`,
                      color: ACCENT,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Full name"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone"
                    value={profilePhone}
                    onChange={(e) => setProfilePhone(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    value={user.email || ""}
                    disabled
                    fullWidth
                    helperText="Email is your login and can't be changed here."
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                sx={{ mt: 2.5 }}
                onClick={saveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? "Saving…" : "Save changes"}
              </Button>
            </Paper>
          )}

          {section === "password" && (
            <Paper sx={{ p: { xs: 2.5, md: 3 }, mb: 3 }}>
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: ACCENT_SOFT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PersonIcon sx={{ color: ACCENT, fontSize: 18 }} />
                </Box>
                <Typography variant="h6">Change your password</Typography>
                <Chip
                  size="small"
                  label={
                    passwordChangedAt
                      ? `Last changed ${daysAgo(passwordChangedAt)}`
                      : "Never changed"
                  }
                  sx={{ ml: 1, fontSize: 11.5, color: "text.secondary" }}
                  variant="outlined"
                />
              </Stack>
              <Typography
                sx={{ color: "text.secondary", fontSize: 13, mb: 2.5 }}
              >
                Enter your current password and choose a new one (at least 6
                characters).
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Current password"
                    type={showPw ? "text" : "password"}
                    value={cur}
                    onChange={(e) => setCur(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="New password"
                    type={showPw ? "text" : "password"}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPw(!showPw)}
                            edge="end"
                            size="small"
                            aria-label="toggle password"
                          >
                            {showPw ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Confirm new password"
                    type={showPw ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </Grid>
              </Grid>
              <Button
                variant="contained"
                sx={{ mt: 2.5 }}
                onClick={savingPw ? undefined : changePassword}
                disabled={savingPw}
              >
                {savingPw ? "Saving…" : "Update password"}
              </Button>
            </Paper>
          )}

          {/* ── Admin only sections ── */}
          {isAdmin && section === "admin" && (
            <>
              {/* Reset any user's password */}
              <Paper
                sx={{
                  mb: 3,
                  overflow: "hidden",
                  border: "1px solid #f3c8c8",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#fdf2f2",
                    px: { xs: 2.5, md: 3 },
                    py: 1.75,
                    borderBottom: "1px solid #f3c8c8",
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        bgcolor: "#fde2e2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <AdminPanelSettingsIcon
                        sx={{ color: "#dc2626", fontSize: 18 }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ color: "#b91c1c" }}>
                      Reset a user's password
                    </Typography>
                  </Stack>
                </Box>
                <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Typography
                    sx={{ color: "text.secondary", fontSize: 13, mb: 2.5 }}
                  >
                    Admin only. Choose a user and set a new password for them.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        label="User"
                        value={targetEmail}
                        onChange={(e) => setTargetEmail(e.target.value)}
                      >
                        <MenuItem value="">— Select a user —</MenuItem>
                        {users.map((u) => (
                          <MenuItem key={u.email} value={u.email}>
                            {u.name ? `${u.name} (${u.email})` : u.email} ·{" "}
                            {roleLabels[u.role] || u.role}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="New password"
                        type={showReset ? "text" : "password"}
                        value={resetPw}
                        onChange={(e) => setResetPw(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowReset(!showReset)}
                                edge="end"
                                size="small"
                                aria-label="toggle password"
                              >
                                {showReset ? (
                                  <VisibilityOff fontSize="small" />
                                ) : (
                                  <Visibility fontSize="small" />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<LockResetIcon />}
                    sx={{ mt: 2.5 }}
                    onClick={requestReset}
                    disabled={resetting}
                  >
                    Reset password
                  </Button>
                  {/* Honest disclosure, per the redesign audit: the prototype's
                intended flow is a one-time email link where the admin never
                sees or sets the password. That requires backend email
                infrastructure (SMTP + token model) that does not currently
                exist anywhere in this application — so this button still
                directly sets the password, exactly as the existing,
                working backend endpoint does today. Not changed silently. */}
                  <Typography
                    sx={{ fontSize: 11.5, color: "text.secondary", mt: 1 }}
                  >
                    This sets their password directly and immediately — there is
                    no reset-link email step.
                  </Typography>
                </Box>
              </Paper>

              {/* Deactivate a user — new shortcut, reuses the exact same
              DELETE /account/users/{id} endpoint and business rule already
              used by Users.jsx (self-deactivation is blocked server-side;
              the current user is also filtered out of this dropdown as an
              extra safety net so the error can't even be triggered from
              here). Copy is accurate to what actually happens — the
              prototype's own text ("reassigns their open roles") is FALSE
              for this backend, which has no job-reassignment logic at all,
              so it is deliberately not used. */}
              <Paper
                sx={{
                  mb: 3,
                  overflow: "hidden",
                  border: "1px solid #f3c8c8",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#fdf2f2",
                    px: { xs: 2.5, md: 3 },
                    py: 1.75,
                    borderBottom: "1px solid #f3c8c8",
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        bgcolor: "#fde2e2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PersonOffOutlinedIcon
                        sx={{ color: "#dc2626", fontSize: 18 }}
                      />
                    </Box>
                    <Typography variant="h6" sx={{ color: "#b91c1c" }}>
                      Deactivate a user
                    </Typography>
                  </Stack>
                </Box>
                <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Typography
                    sx={{ color: "text.secondary", fontSize: 13, mb: 2.5 }}
                  >
                    Removes their access to the portal immediately. Their
                    existing candidates and positions are not reassigned —
                    they'll still show this person as the recruiter until you
                    reassign them manually.
                  </Typography>
                  <TextField
                    select
                    label="User"
                    value={deactivateTarget}
                    onChange={(e) => setDeactivateTarget(e.target.value)}
                    sx={{ maxWidth: 340 }}
                  >
                    <MenuItem value="">— Select a user —</MenuItem>
                    {users
                      .filter((u) => u.email !== user.email)
                      .map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name ? `${u.name} (${u.email})` : u.email} ·{" "}
                          {roleLabels[u.role] || u.role}
                        </MenuItem>
                      ))}
                  </TextField>
                  <Box>
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ mt: 2.5 }}
                      onClick={() => {
                        if (!deactivateTarget) {
                          toast.error("Select a user to deactivate");
                          return;
                        }
                        setConfirmingDeactivate(true);
                      }}
                      disabled={deactivating}
                    >
                      Deactivate…
                    </Button>
                  </Box>
                </Box>
              </Paper>

              {/* Create new user */}
              <Paper sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: ACCENT_SOFT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <PersonAddIcon sx={{ color: ACCENT, fontSize: 18 }} />
                  </Box>
                  <Typography variant="h6">Create a new user</Typography>
                </Stack>
                <Typography
                  sx={{ color: "text.secondary", fontSize: 13, mb: 2.5 }}
                >
                  Admin only. Fill in the details below to create a new account
                  and assign their role. For a Client (SPOC), also enter their
                  company — their name must match the SPOC name on their
                  positions.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Full name"
                      value={newUser.name}
                      onChange={(e) =>
                        setNewUser({ ...newUser, name: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email address"
                      type="email"
                      value={newUser.email}
                      onChange={(e) =>
                        setNewUser({ ...newUser, email: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Password"
                      type={showNewPw ? "text" : "password"}
                      value={newUser.password}
                      onChange={(e) =>
                        setNewUser({ ...newUser, password: e.target.value })
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowNewPw(!showNewPw)}
                              edge="end"
                              size="small"
                              aria-label="toggle password"
                            >
                              {showNewPw ? (
                                <VisibilityOff fontSize="small" />
                              ) : (
                                <Visibility fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Role"
                      value={newUser.role}
                      onChange={(e) =>
                        setNewUser({ ...newUser, role: e.target.value })
                      }
                    >
                      <MenuItem value="">— Select a role —</MenuItem>
                      <MenuItem value="ADMIN">Administrator</MenuItem>
                      <MenuItem value="MANAGER">HR Manager</MenuItem>
                      <MenuItem value="STAFF">Staff</MenuItem>
                      <MenuItem value="RECRUITER">Recruiter</MenuItem>
                      <MenuItem value="CLIENT">Client</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Company — only relevant for a Client (SPOC) account */}
                  {newUser.role === "CLIENT" && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Company"
                        value={newUser.company}
                        onChange={(e) =>
                          setNewUser({ ...newUser, company: e.target.value })
                        }
                        placeholder="Must match the company on their positions"
                      />
                    </Grid>
                  )}
                </Grid>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  sx={{
                    mt: 2.5,
                    bgcolor: NAVY,
                    "&:hover": { bgcolor: NAVY, opacity: 0.9 },
                  }}
                  onClick={creating ? undefined : handleCreateUser}
                  disabled={creating}
                >
                  {creating ? "Creating…" : "Create user"}
                </Button>
              </Paper>
            </>
          )}
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmingReset}
        title="Reset this user's password?"
        message={`This immediately sets a new password for ${targetEmail || "this user"}. They will need the new password you entered to log in — make sure you've shared it with them through a secure channel.`}
        confirmLabel="Reset password"
        destructive
        loading={resetting}
        onConfirm={adminReset}
        onClose={() => setConfirmingReset(false)}
      />

      <ConfirmDialog
        open={confirmingDeactivate}
        title="Deactivate this user?"
        message="This immediately removes their access to the portal. Their existing candidates and positions are not reassigned automatically."
        confirmLabel="Deactivate"
        destructive
        loading={deactivating}
        onConfirm={deactivateUser}
        onClose={() => setConfirmingDeactivate(false)}
      />
    </PageContainer>
  );
}
